<script lang="ts" setup>
import type { NavigationMenuItem } from "@nuxt/ui"
import type { RouteRecordNormalized } from "vue-router"
import type { FleetRole } from "~/enums"

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const routes = router.getRoutes()
const sidebarRoutes = ref(routes.filter((route: RouteRecordNormalized) => !!route.meta.text))
const { hasRole } = useUserContext()

// Highlight the matching sidebar entry not only on exact path but also on
// nested routes — vue-router's aria-current="page" only fires on exact
// match, so /fleets/<id>/devices/<id> would otherwise drop the
// "Flotten" highlight. Tailwind v4 uses suffix `!` for important, so
// these classes win over the link slot's hover/base classes.
const activeLinkClass =
	"bg-white/15! text-white! font-semibold! shadow-[inset_3px_0_0_var(--ui-secondary)]"

// "Flotten" lives on the index route (`/`) but the user's mental model
// includes everything under /fleets too. Map item paths to the set of
// route prefixes that should highlight that item. Anything not in here
// uses the default "self or self/*" rule.
const customActivePrefixes: Record<string, string[]> = {
	"/": ["/fleets"],
}

const isPathActive = (itemPath: string): boolean => {
	if (!itemPath.startsWith("/")) return false
	const current = route.path
	if (current === itemPath) return true
	// Don't let "/" prefix-match everything — only its declared aliases below.
	if (itemPath !== "/" && current.startsWith(`${itemPath}/`)) return true
	const aliases = customActivePrefixes[itemPath] ?? []
	return aliases.some((p) => current === p || current.startsWith(`${p}/`))
}

const allowedSidebarRoutes = computed(() => {
	const filteredSidebarRoutes = []
	for (const sidebarRoute of sidebarRoutes.value) {
		const requiredRole = sidebarRoute.meta.role as FleetRole
		const isPermitted = !requiredRole || (!!requiredRole && hasRole(requiredRole))
		if (isPermitted) filteredSidebarRoutes.push(sidebarRoute)
	}

	const mappedRoutes: NavigationMenuItem[] = filteredSidebarRoutes
		.sort((a, b) => (a.meta.index as number) - (b.meta.index as number))
		.map((sidebarRoute) => ({
			label: t(sidebarRoute.meta.text as string),
			to: sidebarRoute.path,
			icon: sidebarRoute.meta.icon as string,
			class: isPathActive(sidebarRoute.path) ? activeLinkClass : undefined
		}))

	mappedRoutes.push({
		label: t("navigation.documentation"),
		to: "https://documentation.omnect.conplement.cloud/",
		icon: "i-mdi-file-document-outline",
		target: "_blank",
	})

	return mappedRoutes
})
</script>

<template>
	<UTheme :ui="{
		dashboardSidebar: {
			root: 'border-r-0 bg-primary text-white',
			content: 'bg-primary text-white',
			header: 'h-14 px-4 border-b border-white/10 text-white/80 text-xs font-semibold tracking-wider uppercase',
			body: 'px-2 py-3',
			footer: 'px-4 py-3 border-t border-white/10 text-xs text-white/50'
		},
		navigationMenu: {
			link: 'text-white/80 hover:text-white hover:bg-white/5 rounded-lg px-3 py-2.5 gap-3 text-sm font-medium transition-colors',
			linkLeadingIcon: 'size-5 text-white/70'
		}
	}">
		<UDashboardSidebar id="main" collapsible :default-size="20" :data-cy="'sidebar'">
			<template #header="{ collapsed }">
				<span v-if="!collapsed">Navigation</span>
			</template>
			<template #default="{ collapsed }">
				<UNavigationMenu orientation="vertical" :collapsed="collapsed" :items="allowedSidebarRoutes"
					data-cy="main-nav" highlight highlight-color="secondary" />
			</template>
		</UDashboardSidebar>
	</UTheme>
</template>
